import { useNavigate } from 'react-router-dom';
import { useSubscriptionGate } from '@/hooks/useSubscriptionGate';

/**
 * SubscriptionBanner — PAY-001
 * Banner não-intrusivo no topo do layout. Só aparece quando há algo a comunicar.
 *
 * - isExpired → vermelho: "Sua assinatura expirou"
 * - isTrial → amarelo: "Período trial — X dias restantes"
 * - usage > 80% → azul: "Você usou X% do plano"
 * - Tudo ok → null (não renderiza nada)
 */
export function SubscriptionBanner() {
  const navigate = useNavigate();
  const {
    isExpired,
    isTrial,
    isActive,
    usagePercent,
    trialDaysLeft,
    plan,
    isLoading,
  } = useSubscriptionGate();

  // Não mostra nada enquanto carrega
  if (isLoading) return null;

  // ── Expirado ──
  if (isExpired) {
    return (
      <div
        data-testid="subscription-banner"
        className="w-full px-4 py-2.5 flex items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(90deg, rgba(239,68,68,0.15) 0%, rgba(239,68,68,0.08) 100%)',
          borderBottom: '1px solid rgba(239,68,68,0.3)',
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-red-400 text-sm flex-shrink-0">⚠️</span>
          <p className="text-red-300 text-sm truncate">
            Sua assinatura expirou — reative para continuar usando todas as funcionalidades
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/billing')}
          className="flex-shrink-0 px-4 py-1.5 text-xs font-semibold rounded-lg
            bg-red-500/20 text-red-300 border border-red-500/40
            hover:bg-red-500/30 hover:text-red-200 transition-all"
        >
          Reativar
        </button>
      </div>
    );
  }

  // ── Trial ──
  if (isTrial) {
    return (
      <div
        data-testid="subscription-banner"
        className="w-full px-4 py-2.5 flex items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(90deg, rgba(234,179,8,0.12) 0%, rgba(234,179,8,0.05) 100%)',
          borderBottom: '1px solid rgba(234,179,8,0.25)',
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-yellow-400 text-sm flex-shrink-0">⏳</span>
          <p className="text-yellow-300/90 text-sm truncate">
            Período trial — <strong>{trialDaysLeft}</strong> {trialDaysLeft === 1 ? 'dia restante' : 'dias restantes'}
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/billing')}
          className="flex-shrink-0 px-4 py-1.5 text-xs font-semibold rounded-lg
            bg-yellow-500/20 text-yellow-300 border border-yellow-500/40
            hover:bg-yellow-500/30 hover:text-yellow-200 transition-all"
        >
          Assinar agora
        </button>
      </div>
    );
  }

  // ── Uso alto (>80%) ──
  if (isActive && usagePercent >= 80) {
    return (
      <div
        data-testid="subscription-banner"
        className="w-full px-4 py-2.5 flex items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(90deg, rgba(59,130,246,0.12) 0%, rgba(59,130,246,0.05) 100%)',
          borderBottom: '1px solid rgba(59,130,246,0.25)',
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-blue-400 text-sm flex-shrink-0">📊</span>
          <p className="text-blue-300/90 text-sm truncate">
            Você usou <strong>{usagePercent}%</strong> do seu plano <span className="uppercase font-semibold">{plan}</span> este mês
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/billing')}
          className="flex-shrink-0 px-4 py-1.5 text-xs font-semibold rounded-lg
            bg-blue-500/20 text-blue-300 border border-blue-500/40
            hover:bg-blue-500/30 hover:text-blue-200 transition-all"
        >
          Ver plano
        </button>
      </div>
    );
  }

  // ── Tudo ok ──
  return null;
}
